import { ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/use-auth';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireManager?: boolean;
  requiredPermission?: string;
}

export function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requireManager = false,
  requiredPermission
}: ProtectedRouteProps) {
  const { user, loading, isAdmin, isManager, hasPermission, userRole } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      console.log('[ProtectedRoute] Access check:', {
        user: user.email,
        userRole,
        isAdmin,
        isManager,
        requireAdmin,
        requireManager,
        requiredPermission,
        hasPermission: requiredPermission ? hasPermission(requiredPermission) : undefined
      });
    }
  }, [loading, user, userRole, isAdmin, isManager, requireAdmin, requireManager, requiredPermission, hasPermission]);

  // Show loading while auth is initializing OR while role is being determined
  if (loading || (user && userRole === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to login');
    return <Navigate to="/admin/login" replace />;
  }

  // Permission-based check - only deny if role is determined and doesn't have permission
  if (requiredPermission && userRole !== null && !hasPermission(requiredPermission)) {
    console.log('[ProtectedRoute] Permission denied:', requiredPermission);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You do not have the required permissions.</p>
        </div>
      </div>
    );
  }

  // Admin-only check - only deny if role is determined and not admin
  if (requireAdmin && userRole !== null && !isAdmin) {
    console.log('[ProtectedRoute] Admin access denied for user:', user.email, 'role:', userRole);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You do not have admin privileges.</p>
        </div>
      </div>
    );
  }

  // Manager OR admin check - only deny if role is determined and not manager/admin
  if (requireManager && userRole !== null && !isManager && !isAdmin) {
    console.log('[ProtectedRoute] Manager access denied for user:', user.email, 'role:', userRole);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You do not have manager or admin privileges.</p>
        </div>
      </div>
    );
  }

  console.log('[ProtectedRoute] Access granted');
  return <>{children}</>;
}
